import { useState } from 'react';
import { Box, Button, DialogContent, Stack, Typography } from '@mui/material';
import { Styles } from '@t2c/shared-fe';
import { CustomDialogContainer, CustomDialogTitle } from 'apps/t2c-fe/Common/dialog';
import { useRouter } from 'next/router';

const priceCardsData = [
  { credits: 1000000, price: '$5', name: 'Addon 1M Plan', planUniqueId: 'addon-1m-plan' },
  { credits: 5000000, price: '$25', name: 'Addon 5M Plan', planUniqueId: 'addon-5m-plan' },
  { credits: 10000000, price: '$50', name: 'Addon 10M Plan', planUniqueId: 'addon-10m-plan' },
  { credits: 100000000, price: '$500', name: 'Addon 100M Plan', planUniqueId: 'addon-100m-plan' },
];

const PriceCard = ({
  credits,
  price,
  isSelected,
  onSelect,
}: {
  credits: string;
  price: string;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  return (
    <Box
      sx={{
        ...styles.priceCard,
        border: isSelected ? '2px solid #7B35EF' : '2px solid transparent',
        cursor: 'pointer',
      }}
      onClick={onSelect}
    >
      <Stack sx={styles.priceCardContent}>
        <Typography sx={styles.credits}>{credits}</Typography>
        <Typography sx={styles.price}>Price: {price}</Typography>
      </Stack>
    </Box>
  );
};

export const AddonCreditsModal = ({
  openModal = false,
  closeModal,
}: {
  openModal: boolean;
  closeModal: () => void;
}) => {
  const [selectedPriceCard, setSelectedPriceCard] = useState<number>(priceCardsData[0].credits);
  const { push } = useRouter();

  const handlePurchaseClick = () => {
    const selectedCard = priceCardsData.find((card) => card.credits === selectedPriceCard);
    if (selectedCard) {
      closeModal();
      push(`/subscription?name=${selectedCard.planUniqueId}&from=last`);
    }
  };

  return (
    <CustomDialogContainer
      open={openModal}
      maxWidth="sm"
      sx={styles.creditsModal}
      onClose={closeModal}
    >
      <DialogContent dividers sx={styles.dialogContent}>
        <Box sx={styles.addonCreditsModal}>
          <Stack sx={styles.header}>
            <Typography sx={styles.title}>Add Credits</Typography>
            <Typography sx={styles.headerLine}>Add more credits to your current plan</Typography>
          </Stack>
          <Stack sx={styles.priceCards}>
            {priceCardsData.map((card) => (
              <PriceCard
                key={card.credits}
                credits={`${card.credits / 1000000}M`}
                price={card.price}
                isSelected={selectedPriceCard === card.credits}
                onSelect={() => setSelectedPriceCard(card.credits)}
              />
            ))}
          </Stack>
          <Stack sx={styles.buttons}>
            <Button sx={styles.cancelBtn} onClick={closeModal}>
              Cancel
            </Button>
            <Button
              sx={styles.purchaseBtn}
              disabled={!selectedPriceCard}
              onClick={handlePurchaseClick}
            >
              Purchase
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </CustomDialogContainer>
  );
};

const styles: Styles = {
  creditsModal: {
    gap: 2,
    padding: '25px',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '40px',
  },
  dialogContent: {
    padding: '24px',
    paddingTop: '10px',
    display: 'flex',
    flexDirection: 'column',
    background: (theme) => theme.palette.background.secondary,
  },
  priceCard: {
    backgroundColor: '#211B58',
    height: '90px',
    width: '119px',
    borderRadius: '8px',
    padding: '16px',
    transition: 'border 0.2s ease-in-out',
  },
  priceCardContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  priceCards: {
    display: 'grid',
    gridTemplateColumns: {
      xs: 'repeat(2, 1fr)',
      sm: 'repeat(4, 1fr)',
    },
    gap: 1,
    marginBottom: '40px',
  },
  addonCreditsModal: {
    padding: '25px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontWeight: 700,
    fontSize: '26px',
    lineHeight: '36px',
  },
  headerLine: {
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '17px',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'row',
    gap: 2,
  },
  cancelBtn: {
    width: '108px',
    height: '45px',
    borderRadius: '6px',
    color: '#ffffff',
    border: '1px solid #7B35EF',
  },
  purchaseBtn: {
    width: '108px',
    color: '#ffffff',
    height: '45px',
    borderRadius: '6px',
    backgroundColor: '#7B35EF',
    transition: 'background-color 0.2s ease-in-out',
  },
  credits: {
    fontWeight: 700,
    fontSize: '20px',
    lineHeight: '28px',
  },
  price: {
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '22px',
  },
};
